export interface GoProMetadata {
    cameraId: string;
    videoDate?: Date;
    cameraModel?: string;
    cameraMode?: string;
    mediaID: string;
}

export class GoProMetaDataReader {
    hdr_bytes: Uint8Array = new Uint8Array();
    file: File | null = null;
    reader: FileReader;
    reader2: FileReader;
    mdat_offset = 0;
    gpmf_offset = 0;
    gpmf_size = 0;
    callback: (result: GoProMetadata | string) => void;

    constructor(file: File, callback: (result: GoProMetadata | string) => void) {
        this.callback = callback;
        this.reader = new FileReader();
        this.reader2 = new FileReader();

        // Bind event handlers to maintain proper 'this' context
        this.fileHeaderLoad = this.fileHeaderLoad.bind(this);
        this.fileLoad = this.fileLoad.bind(this);
        this.reader.addEventListener('load', this.fileHeaderLoad, false);
        this.reader2.addEventListener('load', this.fileLoad, false);
        this.setFile(file)
    }

    setFile(file: File) {
        // read header
        this.file = file;
        console.log("file.name", file.name);
        console.log("file.size (bytes)", file.size);

        this.mdat_offset = 0;
        this.gpmf_offset = 0;
        this.gpmf_size = 0;

        const blob = file.slice(0, 1024);
        this.reader.readAsArrayBuffer(blob);
    }

    fileHeaderLoad() {
        console.log("fileHeaderLoad")
        if (!this.reader.result || !this.file) {
            this.callback("error");
            return;
        };

        // find the moov offset (from mdat)
        this.hdr_bytes = new Uint8Array(this.reader.result as ArrayBuffer);
        for (let i = 0; i < 60; i++) {
            if (this.hdr_bytes[i] == 109 /*'m'*/ &&
                this.hdr_bytes[i + 1] == 100 /*'d'*/ &&
                this.hdr_bytes[i + 2] == 97 /*'a'*/ &&
                this.hdr_bytes[i + 3] == 116 /*'t'*/) {

                if (this.hdr_bytes[i - 4] == 0 &&
                    this.hdr_bytes[i - 3] == 0 &&
                    this.hdr_bytes[i - 2] == 0 &&
                    this.hdr_bytes[i - 1] == 1) { // 64-bit offset 
                    this.mdat_offset = (this.hdr_bytes[i + 7] * 4294967296);
                    this.mdat_offset += (this.hdr_bytes[i + 8] * 16777216);
                    this.mdat_offset += (this.hdr_bytes[i + 9] << 16) +
                        (this.hdr_bytes[i + 10] << 8) +
                        (this.hdr_bytes[i + 11]) + i - 4;
                } else {
                    this.mdat_offset = (this.hdr_bytes[i - 4] * 16777216);
                    this.mdat_offset += (this.hdr_bytes[i - 3] << 16) +
                        (this.hdr_bytes[i - 2] << 8) +
                        (this.hdr_bytes[i - 1]) + i - 4;
                }
            }
        }

        // get only the interested part
        const blob = this.file.slice(this.mdat_offset, this.mdat_offset + (1024 * 1024 * 24));
        this.reader2.readAsArrayBuffer(blob);
    }

    async fileLoad() {
        try {
            if (this.mdat_offset == 0 || !this.reader2.result) {
                console.log("fileLoad error")
                this.callback("undefined");
                return;
            };

            const bytes = new Uint8Array(this.reader2.result as ArrayBuffer);
            let udta_offset = 0;

            // find udta
            for (let i = 0; i < 1024 * 1024 * 24; i++) {
                if (bytes[i] == 117 /*'u'*/ &&
                    bytes[i + 1] == 100 /*'d'*/ &&
                    bytes[i + 2] == 116 /*'t'*/ &&
                    bytes[i + 3] == 97 /*'a'*/) {
                    udta_offset = i;
                    break;
                }
            }

            if (udta_offset) {
                // find GPMF
                for (let i = udta_offset; i < udta_offset + 4096; i++) {
                    if (bytes[i] == 0x47 /*'G'*/ &&
                        bytes[i + 1] == 0x50 /*'P'*/ &&
                        bytes[i + 2] == 0x4D /*'M'*/ &&
                        bytes[i + 3] == 0x46 /*'F'*/) {
                        this.gpmf_offset = i + 4;
                        this.gpmf_size = (bytes[i - 3] << 16) +
                            (bytes[i - 2] << 8) +
                            (bytes[i - 1]) - 8;
                        break;
                    }
                }
            }
            else {
                if (!this.reader.result || !this.file) {
                    this.callback("error");
                    return;
                };
            }

            let label = "";
            let value = "";
            let cameraId = "";
            let videoDate: Date | undefined = undefined;
            let cameraModel: string | undefined = undefined;
            let cameraMode: string | undefined = undefined;
            let mediaID: string | undefined = undefined;

            for (let i = this.gpmf_offset; i < this.gpmf_offset + this.gpmf_size;) {
                label = "";
                value = "";

                let fourcc = (bytes[i] * 16777216);
                fourcc += (bytes[i + 1] << 16) +
                    (bytes[i + 2] << 8) +
                    (bytes[i + 3]);

                if (fourcc == 0) break;

                const type = bytes[i + 4];
                const typsize = bytes[i + 5];
                const repeat = (bytes[i + 6] << 8) + bytes[i + 7];
                const size = typsize * repeat;
                const align_size = Math.trunc((size + 3) / 4) * 4;

                label += String.fromCharCode(bytes[i]);
                label += String.fromCharCode(bytes[i + 1]);
                label += String.fromCharCode(bytes[i + 2]);
                label += String.fromCharCode(bytes[i + 3]);

                if (type == 0) {
                    i += 8;
                } else {
                    // Look for camera serial number
                    if (label == "CASN") {
                        if (type == 0x63 /* c */) {
                            for (let j = 0; j < size; j++) {
                                if (bytes[i + 8 + j] != 0 && bytes[i + 8 + j] != 10 && bytes[i + 8 + j] != 13) {
                                    value += String.fromCharCode(bytes[i + 8 + j]);
                                }
                            }
                            cameraId = value;
                            console.log("Found camera ID:", cameraId);
                        }
                    }
                    // Look for media unique ID (MUID)
                    else if (label == "MUID") {
                        if (type == 0x4C /* L */) {
                            let hexValue = "";

                            for (let j = 0; j < size; j++) {
                                const b = bytes[i + 8 + j];

                                if (b !== 0 && b !== 10 && b !== 13) {
                                    hexValue += b.toString(16).padStart(2, "0");
                                }
                            }

                            mediaID = hexValue;
                            console.log("Found media unique ID:", mediaID);
                        }
                    }
                    // Look for camera model information (MINF)
                    else if (label == "MINF") {
                        if (type == 0x63 /* c */) {
                            for (let j = 0; j < size; j++) {
                                if (bytes[i + 8 + j] != 0 && bytes[i + 8 + j] != 10 && bytes[i + 8 + j] != 13) {
                                    value += String.fromCharCode(bytes[i + 8 + j]);
                                }
                            }
                            cameraModel = value;
                            console.log("Found camera model:", cameraModel);
                        }
                    }
                    // Look for field of view mode (VFOV)
                    else if (label == "VFOV") {
                        if (type == 0x63 /* c */) {
                            for (let j = 0; j < size; j++) {
                                if (bytes[i + 8 + j] != 0 && bytes[i + 8 + j] != 10 && bytes[i + 8 + j] != 13) {
                                    value += String.fromCharCode(bytes[i + 8 + j]);
                                }
                            }
                            // Map VFOV codes to readable mode names
                            const fovMap: { [key: string]: string } = {
                                'L': 'Linear',
                                'W': 'Wide',
                                'S': 'SuperView',
                                'H': 'HyperView',
                                'N': 'Narrow'
                            };
                            cameraMode = fovMap[value] || value;
                            console.log("Found camera mode:", cameraMode);
                        }
                    }

                    i += 8 + align_size;
                }
            }

            // If no date found in GPMF, try to use file modification date as fallback
            if (!videoDate && this.file) {
                videoDate = new Date(this.file.lastModified);
                console.log("Using file modification date as fallback:", videoDate);
            }

            // After parsing all metadata, return the result
            if (cameraId) {
                if (cameraModel == "GoPro Max"){
                    const rawInput = cameraId + this.file?.name + String(this.file?.size);
                    const encoded = new TextEncoder().encode(rawInput);
                    const hashBuffer = await crypto.subtle.digest('SHA-256', encoded);
                    const hashArray = Array.from(new Uint8Array(hashBuffer));
                    mediaID = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
                    console.log("Generate new MUID :", mediaID)
                }

                const result: GoProMetadata = {
                    cameraId: cameraId,
                    videoDate: videoDate,
                    cameraModel: cameraModel,
                    cameraMode: cameraMode,
                    mediaID: mediaID || "unknown",
                };
                console.log("Returning result:", result);
                this.callback(result);
                return;
            }

            console.log("could not find camera ID")
            this.callback("undefined")
        } catch (e) {
            console.error(e)
            this.callback("error")
        }
    }
}
